import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { LogOut, ChevronLeft, Edit, FileText, Download, User, Users, Heart, Briefcase, Shield, Mail, Phone, MapPin, ScrollText, Gavel, Loader2, Plus, Trash2, MessageSquare, Calendar, Clock, CheckCircle, Star, ChevronDown, X, Save, Pencil, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phone number formatting function
function formatPhoneNumber(value) {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 10);
    if (limited.length === 0) return '';
    if (limited.length <= 3) return '(' + limited;
    if (limited.length <= 6) return '(' + limited.slice(0, 3) + ') ' + limited.slice(3);
    return '(' + limited.slice(0, 3) + ') ' + limited.slice(3, 6) + '-' + limited.slice(6);
}

function ClientDetail() {
    const params = useParams();
    const id = params.id;
    const auth = useAuth();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [docs, setDocs] = useState([]);
    const [communications, setCommunications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gen, setGen] = useState(false);
    const [showAddComm, setShowAddComm] = useState(false);
    const [users, setUsers] = useState([]);
    const [showAssignDropdown, setShowAssignDropdown] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templateRecommendation, setTemplateRecommendation] = useState(null);
    const [selectedTemplates, setSelectedTemplates] = useState({
        wills: 'will',
        trusts: 'trust',
        poa: 'poa',
        amd: 'living_will',
        other: 'letter'
    });
    const [newComm, setNewComm] = useState({
        contact_type: 'call',
        contact_date: new Date().toISOString().split('T')[0],
        notes: '',
        deliverable: '',
        due_date: '',
        completed: false
    });
    
    // Quick Edit State
    const [editSection, setEditSection] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [staffList, setStaffList] = useState({ attorneys: [], intake_coordinators: [] });

    useEffect(() => {
        loadClient();
        loadDocs();
        loadCommunications();
        loadUsers();
        loadTemplates();
        loadStaffList();
        loadTemplateRecommendation();
    }, [id]);
    
    async function loadStaffList() {
        try {
            const res = await axios.get(API_URL + '/api/users/staff');
            setStaffList(res.data);
        } catch (e) {
            console.error('Could not load staff list');
        }
    }

    async function loadTemplateRecommendation() {
        try {
            const res = await axios.get(API_URL + '/api/clients/' + id + '/template-recommendation');
            setTemplateRecommendation(res.data.recommendation);
            // Auto-select the recommended will template
            if (res.data.recommendation?.template_name) {
                setSelectedTemplates(prev => ({...prev, wills: res.data.recommendation.template_name}));
            }
        } catch (e) {
            console.error('Could not load template recommendation');
        }
    }
    
    function openEdit(section) {
        setEditSection(section);
        // Initialize edit data based on section
        switch(section) {
            case 'intake':
                setEditData({
                    status: client.status || 'active_retained',
                    attorney_assigned: client.attorney_assigned || '',
                    intake_coordinator: client.intake_coordinator || '',
                    referral_source: client.referral_source || '',
                    consultation_date: client.consultation_date || '',
                    fee_amount: client.fee_amount || '',
                    services_requested: client.services_requested || {},
                    intake_notes: client.intake_notes || ''
                });
                break;
            case 'attorney':
                setEditData({
                    attorney_recommendation: client.attorney_recommendation || '',
                    recommendation_reason: client.recommendation_reason || '',
                    cost_quoted: client.cost_quoted || ''
                });
                break;
            case 'personal':
                setEditData({
                    first_name: client.first_name || '',
                    middle_name: client.middle_name || '',
                    last_name: client.last_name || '',
                    date_of_birth: client.date_of_birth || '',
                    email: client.email || '',
                    phone: client.phone || '',
                    address: client.address || '',
                    city: client.city || '',
                    state: client.state || '',
                    zip_code: client.zip_code || '',
                    marital_status: client.marital_status || 'single',
                    locality_type: client.locality_type || 'county',
                    locality_name: client.locality_name || ''
                });
                break;
            case 'spouse':
                setEditData({
                    spouse: { ...(client.spouse || { name: '', date_of_birth: '', email: '', phone: '', gender: '' }) }
                });
                break;
            case 'executor':
                setEditData({
                    executor: { ...(client.executor || { primary_name: '', primary_phone: '', primary_relationship: '', primary_gender: '', alternate_name: '', alternate_relationship: '', alternate_gender: '' }) }
                });
                break;
            case 'poa':
                setEditData({
                    power_of_attorney: { ...(client.power_of_attorney || { financial_poa_name: '', financial_poa_phone: '', financial_poa_relationship: '', financial_poa_gender: '', financial_poa_alternate: '', financial_poa_alternate_relationship: '', financial_poa_alternate_gender: '', durable: true, springing: false }) }
                });
                break;
            case 'healthcare':
                setEditData({
                    healthcare_directive: { ...(client.healthcare_directive || { healthcare_agent_name: '', healthcare_agent_phone: '', healthcare_agent_relationship: '', healthcare_agent_gender: '', alternate_agent_name: '', alternate_agent_phone: '', has_living_will: false, dnr_order: false, organ_donor: false }) }
                });
                break;
            case 'final_wishes':
                setEditData({
                    final_wishes: { ...(client.final_wishes || { disposition_method: 'cremation', disposition_instructions: '', distribution_type: 'per_stirpes', disinherited_persons: '', minor_children_guardian: '', pet_care_instructions: '', unusual_requests: '' }) }
                });
                break;
            case 'children':
                setEditData({
                    children: JSON.parse(JSON.stringify(client.children || []))
                });
                break;
            case 'beneficiaries':
                setEditData({
                    beneficiaries: JSON.parse(JSON.stringify(client.beneficiaries || []))
                });
                break;
            case 'assets':
                setEditData({
                    assets: JSON.parse(JSON.stringify(client.assets || []))
                });
                break;
            case 'key_contacts':
                setEditData({
                    spouse: { ...(client.spouse || { name: '', date_of_birth: '', email: '', phone: '', gender: '' }) },
                    power_of_attorney: { ...(client.power_of_attorney || { financial_poa_name: '', financial_poa_phone: '', financial_poa_relationship: '', financial_poa_gender: '' }) },
                    healthcare_directive: { ...(client.healthcare_directive || { healthcare_agent_name: '', healthcare_agent_phone: '', healthcare_agent_relationship: '', healthcare_agent_gender: '' }) },
                    executor: { ...(client.executor || { primary_name: '', primary_phone: '', primary_relationship: '', primary_gender: '' }) }
                });
                break;
            default:
                setEditData({});
        }
    }
    
    async function saveEdit() {
        setSaving(true);
        try {
            const updateData = { ...client, ...editData };
            await axios.put(API_URL + '/api/clients/' + id, updateData);
            await loadClient();
            setEditSection(null);
            toast.success('Changes saved');
        } catch (e) {
            toast.error('Failed to save changes');
        } finally {
            setSaving(false);
        }
    }
    
    function cancelEdit() {
        setEditSection(null);
        setEditData({});
    }

    async function loadTemplates() {
        try {
            const res = await axios.get(API_URL + '/api/templates');
            setTemplates(res.data);
        } catch (e) {
            console.error('Could not load templates');
        }
    }

    async function loadUsers() {
        try {
            const res = await axios.get(API_URL + '/api/admin/users');
            setUsers(res.data);
        } catch (e) {
            // User might not be admin, that's okay
            console.error('Could not load users list');
        }
    }

    async function assignClient(userId) {
        try {
            const res = await axios.patch(API_URL + '/api/clients/' + id + '/assign', {
                assigned_to: userId
            });
            setClient(res.data);
            setShowAssignDropdown(false);
            toast.success(userId ? 'Client assigned successfully' : 'Client unassigned');
        } catch (e) {
            toast.error('Failed to assign client');
        }
    }

    async function loadClient() {
        try {
            const res = await axios.get(API_URL + '/api/clients/' + id);
            setClient(res.data);
        } catch (e) {
            toast.error('Failed to load client');
            navigate('/clients');
        } finally {
            setLoading(false);
        }
    }

    async function loadDocs() {
        try {
            const res = await axios.get(API_URL + '/api/documents', { params: { client_id: id } });
            setDocs(res.data);
        } catch (e) { console.error(e); }
    }

    async function deleteDoc(docId, docTitle) {
        if (!window.confirm('Delete document "' + docTitle + '"? This cannot be undone.')) return;
        try {
            await axios.delete(API_URL + '/api/documents/' + docId);
            toast.success('Document deleted');
            loadDocs();
        } catch (e) {
            if (e.response?.data?.detail) {
                toast.error(e.response.data.detail);
            } else {
                toast.error('Failed to delete document');
            }
        }
    }

    async function toggleFinalize(docId) {
        try {
            await axios.patch(API_URL + '/api/documents/' + docId + '/finalize');
            loadDocs();
        } catch (e) {
            toast.error('Failed to update document');
        }
    }

    async function deleteAllDrafts() {
        const drafts = docs.filter(d => !d.finalized);
        if (drafts.length === 0) {
            toast.info('No drafts to delete');
            return;
        }
        if (!window.confirm('Delete all ' + drafts.length + ' draft document(s)? Starred (final) documents will be kept. This cannot be undone.')) return;
        
        let deleted = 0;
        for (const d of drafts) {
            try {
                await axios.delete(API_URL + '/api/documents/' + d.id);
                deleted++;
            } catch (e) {
                console.error('Failed to delete', d.id);
            }
        }
        toast.success('Deleted ' + deleted + ' draft(s)');
        loadDocs();
    }

    async function loadCommunications() {
        try {
            const res = await axios.get(API_URL + '/api/clients/' + id + '/communications');
            setCommunications(res.data);
        } catch (e) { console.error(e); }
    }

    async function addCommunication() {
        try {
            await axios.post(API_URL + '/api/communications', {
                client_id: id,
                ...newComm
            });
            toast.success('Communication log added');
            setShowAddComm(false);
            setNewComm({
                contact_type: 'call',
                contact_date: new Date().toISOString().split('T')[0],
                notes: '',
                deliverable: '',
                due_date: '',
                completed: false
            });
            loadCommunications();
        } catch (e) {
            toast.error('Failed to add communication log');
        }
    }

    async function toggleComplete(logId) {
        try {
            await axios.patch(API_URL + '/api/communications/' + logId + '/complete');
            loadCommunications();
        } catch (e) {
            toast.error('Failed to update');
        }
    }

    async function deleteComm(logId) {
        if (!window.confirm('Delete this communication log?')) return;
        try {
            await axios.delete(API_URL + '/api/communications/' + logId);
            toast.success('Deleted');
            loadCommunications();
        } catch (e) {
            toast.error('Failed to delete');
        }
    }

    async function genDoc(type) {
        setGen(true);
        try {
            const res = await axios.post(API_URL + '/api/documents/generate', { client_id: id, document_type: type });
            const docId = res.data.id;
            toast.success(
                <span>
                    Document generated - <a href={`/documents/${docId}`} style={{textDecoration: 'underline', fontWeight: 'bold'}}>View Document</a>
                </span>
            );
            loadDocs();
        } catch (e) { toast.error('Failed to generate'); } finally { setGen(false); }
    }

    async function genAllEssentialDocs() {
        setGen(true);
        try {
            const res = await axios.post(API_URL + '/api/documents/generate-multiple', { 
                client_id: id, 
                document_types: ['will', 'poa', 'living_will'] 
            });
            const data = res.data;
            if (data.total_generated > 0) {
                toast.success(`Generated ${data.total_generated} document(s): Will, POA, Advance Medical Directive`);
            }
            if (data.total_failed > 0) {
                toast.error(`Failed to generate ${data.total_failed} document(s)`);
            }
            loadDocs();
        } catch (e) { 
            toast.error('Failed to generate documents'); 
        } finally { 
            setGen(false); 
        }
    }

    function fmtDate(d) { return d ? new Date(d).toLocaleDateString() : 'N/A'; }
    function fmtDateTime(d) { 
        if (!d) return 'N/A';
        const date = new Date(d);
        return date.toLocaleDateString('en-US', { timeZone: 'America/New_York' }) + ' ' + date.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }
    function fmtMoney(n) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0); }
    
    // Format middle name/initial - add period if single letter
    function fmtMiddle(m) { return m && m.trim().length === 1 ? m.trim() + '.' : m; }
    function fmtFullName(first, middle, last) {
        const parts = [first, fmtMiddle(middle), last].filter(Boolean);
        return parts.join(' ');
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!client) return null;

    const totalVal = (client.assets || []).reduce((a, b) => a + (b.estimated_value || 0), 0);

    // Separate pending and completed deliverables
    const pendingDeliverables = communications.filter(c => c.deliverable && !c.completed);
    const completedDeliverables = communications.filter(c => c.deliverable && c.completed);
    
    const el = React.createElement;
    
    // Edit Modal Component using React.createElement
    const EditModal = () => {
        if (!editSection) return null;
        
        const titles = {
            intake: 'Edit Intake Information',
            attorney: 'Edit Attorney Notes',
            personal: 'Edit Personal Information',
            spouse: 'Edit Spouse Information',
            executor: 'Edit Executor Information',
            poa: 'Edit Power of Attorney',
            healthcare: 'Edit Healthcare Directive',
            final_wishes: 'Edit Final Wishes',
            children: 'Edit Children',
            beneficiaries: 'Edit Beneficiaries',
            assets: 'Edit Assets',
            key_contacts: 'Edit Key Contacts'
        };
        
        const renderIntakeForm = () => el('div', { className: 'space-y-4' },
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Status'),
                    el('select', { value: editData.status || '', onChange: e => setEditData({...editData, status: e.target.value}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                        el('option', { value: 'active_retained' }, 'Active & Retained'),
                        el('option', { value: 'active_not_retained' }, 'Active & Not Retained'),
                        el('option', { value: 'inactive' }, 'Inactive')
                    )
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Attorney Assigned'),
                    el('select', { value: editData.attorney_assigned || '', onChange: e => setEditData({...editData, attorney_assigned: e.target.value}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                        el('option', { value: '' }, 'Select Attorney...'),
                        staffList.attorneys.map(a => el('option', { key: a.id, value: a.name }, a.name))
                    )
                )
            ),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Intake Coordinator'),
                    el('select', { value: editData.intake_coordinator || '', onChange: e => setEditData({...editData, intake_coordinator: e.target.value}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                        el('option', { value: '' }, 'Select Coordinator...'),
                        staffList.intake_coordinators.map(ic => el('option', { key: ic.id, value: ic.name }, ic.name))
                    )
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Referral Source'),
                    el(Input, { value: editData.referral_source || '', onChange: e => setEditData({...editData, referral_source: e.target.value}), className: 'rounded-none' })
                )
            ),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Consultation Date'),
                    el(Input, { type: 'date', value: editData.consultation_date || '', onChange: e => setEditData({...editData, consultation_date: e.target.value}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Fee Amount'),
                    el(Input, { value: editData.fee_amount || '', onChange: e => setEditData({...editData, fee_amount: e.target.value}), className: 'rounded-none', placeholder: '$0.00' })
                )
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Intake Notes'),
                el(Textarea, { value: editData.intake_notes || '', onChange: e => setEditData({...editData, intake_notes: e.target.value}), className: 'rounded-none min-h-[80px]' })
            )
        );
        
        const renderAttorneyForm = () => el('div', { className: 'space-y-4' },
            el('div', { className: 'space-y-2' },
                el(Label, null, 'What Was Recommended'),
                el(Textarea, { value: editData.attorney_recommendation || '', onChange: e => setEditData({...editData, attorney_recommendation: e.target.value}), className: 'rounded-none min-h-[80px]', placeholder: 'e.g., Will with Trust, POA, AMD...' })
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Why It Was Recommended'),
                el(Textarea, { value: editData.recommendation_reason || '', onChange: e => setEditData({...editData, recommendation_reason: e.target.value}), className: 'rounded-none min-h-[80px]' })
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Cost Quoted'),
                el(Input, { value: editData.cost_quoted || '', onChange: e => setEditData({...editData, cost_quoted: e.target.value}), className: 'rounded-none w-1/2', placeholder: '$0.00' })
            )
        );
        
        const renderPersonalForm = () => el('div', { className: 'space-y-4' },
            el('div', { className: 'grid grid-cols-3 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'First Name'),
                    el(Input, { value: editData.first_name || '', onChange: e => setEditData({...editData, first_name: e.target.value}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Middle Name'),
                    el(Input, { value: editData.middle_name || '', onChange: e => setEditData({...editData, middle_name: e.target.value}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Last Name'),
                    el(Input, { value: editData.last_name || '', onChange: e => setEditData({...editData, last_name: e.target.value}), className: 'rounded-none' })
                )
            ),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Email'),
                    el(Input, { type: 'email', value: editData.email || '', onChange: e => setEditData({...editData, email: e.target.value}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Phone'),
                    el(Input, { type: 'tel', value: editData.phone || '', onChange: e => setEditData({...editData, phone: formatPhoneNumber(e.target.value)}), className: 'rounded-none', placeholder: '(555) 555-5555' })
                )
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Address'),
                el(Input, { value: editData.address || '', onChange: e => setEditData({...editData, address: e.target.value}), className: 'rounded-none' })
            ),
            el('div', { className: 'grid grid-cols-3 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'City'),
                    el(Input, { value: editData.city || '', onChange: e => setEditData({...editData, city: e.target.value}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'State'),
                    el(Input, { value: editData.state || '', onChange: e => setEditData({...editData, state: e.target.value}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Zip Code'),
                    el(Input, { value: editData.zip_code || '', onChange: e => setEditData({...editData, zip_code: e.target.value}), className: 'rounded-none' })
                )
            )
        );
        
        const renderSpouseForm = () => el('div', { className: 'space-y-4' },
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Spouse Name'),
                    el(Input, { value: editData.spouse?.name || '', onChange: e => setEditData({...editData, spouse: {...editData.spouse, name: e.target.value}}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Pronouns'),
                    el('select', { value: editData.spouse?.gender || '', onChange: e => setEditData({...editData, spouse: {...editData.spouse, gender: e.target.value}}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                        el('option', { value: '' }, 'Select...'),
                        el('option', { value: 'male' }, 'He/Him'),
                        el('option', { value: 'female' }, 'She/Her'),
                        el('option', { value: 'neutral' }, 'They/Them')
                    )
                )
            ),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Phone'),
                    el(Input, { type: 'tel', value: editData.spouse?.phone || '', onChange: e => setEditData({...editData, spouse: {...editData.spouse, phone: formatPhoneNumber(e.target.value)}}), className: 'rounded-none', placeholder: '(555) 555-5555' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Email'),
                    el(Input, { type: 'email', value: editData.spouse?.email || '', onChange: e => setEditData({...editData, spouse: {...editData.spouse, email: e.target.value}}), className: 'rounded-none' })
                )
            )
        );
        
        const renderExecutorForm = () => el('div', { className: 'space-y-4' },
            el('h4', { className: 'font-medium text-sm text-muted-foreground' }, 'PRIMARY EXECUTOR'),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Name'),
                    el(Input, { value: editData.executor?.primary_name || '', onChange: e => setEditData({...editData, executor: {...editData.executor, primary_name: e.target.value}}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Phone'),
                    el(Input, { type: 'tel', value: editData.executor?.primary_phone || '', onChange: e => setEditData({...editData, executor: {...editData.executor, primary_phone: formatPhoneNumber(e.target.value)}}), className: 'rounded-none' })
                )
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Relationship'),
                el(Input, { value: editData.executor?.primary_relationship || '', onChange: e => setEditData({...editData, executor: {...editData.executor, primary_relationship: e.target.value}}), className: 'rounded-none' })
            ),
            el('h4', { className: 'font-medium text-sm text-muted-foreground pt-4' }, 'ALTERNATE EXECUTOR'),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Name'),
                    el(Input, { value: editData.executor?.alternate_name || '', onChange: e => setEditData({...editData, executor: {...editData.executor, alternate_name: e.target.value}}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Relationship'),
                    el(Input, { value: editData.executor?.alternate_relationship || '', onChange: e => setEditData({...editData, executor: {...editData.executor, alternate_relationship: e.target.value}}), className: 'rounded-none' })
                )
            )
        );
        
        const renderPOAForm = () => el('div', { className: 'space-y-4' },
            el('h4', { className: 'font-medium text-sm text-muted-foreground' }, 'PRIMARY POA AGENT'),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Name'),
                    el(Input, { value: editData.power_of_attorney?.financial_poa_name || '', onChange: e => setEditData({...editData, power_of_attorney: {...editData.power_of_attorney, financial_poa_name: e.target.value}}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Phone'),
                    el(Input, { type: 'tel', value: editData.power_of_attorney?.financial_poa_phone || '', onChange: e => setEditData({...editData, power_of_attorney: {...editData.power_of_attorney, financial_poa_phone: formatPhoneNumber(e.target.value)}}), className: 'rounded-none' })
                )
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Relationship'),
                el(Input, { value: editData.power_of_attorney?.financial_poa_relationship || '', onChange: e => setEditData({...editData, power_of_attorney: {...editData.power_of_attorney, financial_poa_relationship: e.target.value}}), className: 'rounded-none' })
            ),
            el('h4', { className: 'font-medium text-sm text-muted-foreground pt-4' }, 'ALTERNATE POA AGENT'),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Name'),
                    el(Input, { value: editData.power_of_attorney?.financial_poa_alternate || '', onChange: e => setEditData({...editData, power_of_attorney: {...editData.power_of_attorney, financial_poa_alternate: e.target.value}}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Relationship'),
                    el(Input, { value: editData.power_of_attorney?.financial_poa_alternate_relationship || '', onChange: e => setEditData({...editData, power_of_attorney: {...editData.power_of_attorney, financial_poa_alternate_relationship: e.target.value}}), className: 'rounded-none' })
                )
            )
        );
        
        const renderHealthcareForm = () => el('div', { className: 'space-y-4' },
            el('h4', { className: 'font-medium text-sm text-muted-foreground' }, 'PRIMARY HEALTHCARE AGENT'),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Name'),
                    el(Input, { value: editData.healthcare_directive?.healthcare_agent_name || '', onChange: e => setEditData({...editData, healthcare_directive: {...editData.healthcare_directive, healthcare_agent_name: e.target.value}}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Phone'),
                    el(Input, { type: 'tel', value: editData.healthcare_directive?.healthcare_agent_phone || '', onChange: e => setEditData({...editData, healthcare_directive: {...editData.healthcare_directive, healthcare_agent_phone: formatPhoneNumber(e.target.value)}}), className: 'rounded-none' })
                )
            ),
            el('h4', { className: 'font-medium text-sm text-muted-foreground pt-4' }, 'ALTERNATE HEALTHCARE AGENT'),
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Name'),
                    el(Input, { value: editData.healthcare_directive?.alternate_agent_name || '', onChange: e => setEditData({...editData, healthcare_directive: {...editData.healthcare_directive, alternate_agent_name: e.target.value}}), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Phone'),
                    el(Input, { type: 'tel', value: editData.healthcare_directive?.alternate_agent_phone || '', onChange: e => setEditData({...editData, healthcare_directive: {...editData.healthcare_directive, alternate_agent_phone: formatPhoneNumber(e.target.value)}}), className: 'rounded-none' })
                )
            )
        );
        
        const renderFinalWishesForm = () => el('div', { className: 'space-y-4' },
            el('div', { className: 'grid grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Disposition Method'),
                    el('select', { value: editData.final_wishes?.disposition_method || 'cremation', onChange: e => setEditData({...editData, final_wishes: {...editData.final_wishes, disposition_method: e.target.value}}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                        el('option', { value: 'cremation' }, 'Cremation'),
                        el('option', { value: 'burial' }, 'Burial')
                    )
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Distribution Type'),
                    el('select', { value: editData.final_wishes?.distribution_type || 'per_stirpes', onChange: e => setEditData({...editData, final_wishes: {...editData.final_wishes, distribution_type: e.target.value}}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                        el('option', { value: 'per_stirpes' }, 'Per Stirpes'),
                        el('option', { value: 'per_capita' }, 'Per Capita')
                    )
                )
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Disposition Instructions'),
                el(Textarea, { value: editData.final_wishes?.disposition_instructions || '', onChange: e => setEditData({...editData, final_wishes: {...editData.final_wishes, disposition_instructions: e.target.value}}), className: 'rounded-none min-h-[60px]' })
            ),
            el('div', { className: 'space-y-2' },
                el(Label, null, 'Pet Care Instructions'),
                el(Textarea, { value: editData.final_wishes?.pet_care_instructions || '', onChange: e => setEditData({...editData, final_wishes: {...editData.final_wishes, pet_care_instructions: e.target.value}}), className: 'rounded-none min-h-[60px]' })
            )
        );
        
        // Children form with add/edit/delete
        const addChild = () => {
            const newChild = { id: String(Date.now()), name: '', date_of_birth: '', minor: false, special_needs: false, disinherited: false };
            setEditData({...editData, children: [...(editData.children || []), newChild]});
        };
        const updateChild = (idx, field, value) => {
            const updated = [...editData.children];
            updated[idx] = {...updated[idx], [field]: value};
            setEditData({...editData, children: updated});
        };
        const removeChild = (idx) => {
            setEditData({...editData, children: editData.children.filter((_, i) => i !== idx)});
        };
        
        const renderChildrenForm = () => el('div', { className: 'space-y-4' },
            el(Button, { type: 'button', variant: 'outline', size: 'sm', onClick: addChild, className: 'rounded-none mb-4' },
                el(Plus, { className: 'h-4 w-4 mr-2' }), 'Add Child'
            ),
            (editData.children || []).length === 0 
                ? el('p', { className: 'text-muted-foreground text-sm' }, 'No children added')
                : (editData.children || []).map((child, idx) => 
                    el('div', { key: child.id || idx, className: 'p-4 border border-stone-200 space-y-3 ' + (child.disinherited ? 'border-red-300 bg-red-50/30' : '') },
                        el('div', { className: 'flex justify-between items-start' },
                            el('span', { className: 'text-sm font-medium' }, 'Child ' + (idx + 1)),
                            el(Button, { type: 'button', variant: 'ghost', size: 'icon', onClick: () => removeChild(idx), className: 'h-8 w-8 text-destructive' },
                                el(Trash2, { className: 'h-4 w-4' })
                            )
                        ),
                        el('div', { className: 'grid grid-cols-2 gap-4' },
                            el('div', { className: 'space-y-2' },
                                el(Label, null, 'Full Name'),
                                el(Input, { value: child.name || '', onChange: e => updateChild(idx, 'name', e.target.value), className: 'rounded-none' })
                            ),
                            el('div', { className: 'space-y-2' },
                                el(Label, null, 'Date of Birth'),
                                el(Input, { type: 'date', value: child.date_of_birth || '', onChange: e => updateChild(idx, 'date_of_birth', e.target.value), className: 'rounded-none' })
                            )
                        ),
                        el('div', { className: 'flex flex-wrap gap-4' },
                            el('label', { className: 'flex items-center gap-2' },
                                el('input', { type: 'checkbox', checked: child.minor || false, onChange: e => updateChild(idx, 'minor', e.target.checked) }),
                                el('span', { className: 'text-sm' }, 'Minor')
                            ),
                            el('label', { className: 'flex items-center gap-2' },
                                el('input', { type: 'checkbox', checked: child.special_needs || false, onChange: e => updateChild(idx, 'special_needs', e.target.checked) }),
                                el('span', { className: 'text-sm' }, 'Special Needs')
                            ),
                            el('label', { className: 'flex items-center gap-2' },
                                el('input', { type: 'checkbox', checked: child.disinherited || false, onChange: e => updateChild(idx, 'disinherited', e.target.checked) }),
                                el('span', { className: 'text-sm text-red-600' }, 'Disinherit')
                            )
                        )
                    )
                )
        );
        
        // Beneficiaries form with add/edit/delete
        const addBeneficiary = () => {
            const newBen = { id: String(Date.now()), name: '', relationship: '', percentage: 0, contingent: false };
            setEditData({...editData, beneficiaries: [...(editData.beneficiaries || []), newBen]});
        };
        const updateBeneficiary = (idx, field, value) => {
            const updated = [...editData.beneficiaries];
            updated[idx] = {...updated[idx], [field]: value};
            setEditData({...editData, beneficiaries: updated});
        };
        const removeBeneficiary = (idx) => {
            setEditData({...editData, beneficiaries: editData.beneficiaries.filter((_, i) => i !== idx)});
        };
        
        const renderBeneficiariesForm = () => {
            const primary = (editData.beneficiaries || []).filter(b => !b.contingent);
            const contingent = (editData.beneficiaries || []).filter(b => b.contingent);
            const primaryTotal = primary.reduce((sum, b) => sum + (parseFloat(b.percentage) || 0), 0);
            const contingentTotal = contingent.reduce((sum, b) => sum + (parseFloat(b.percentage) || 0), 0);
            
            return el('div', { className: 'space-y-4' },
                el(Button, { type: 'button', variant: 'outline', size: 'sm', onClick: addBeneficiary, className: 'rounded-none mb-4' },
                    el(Plus, { className: 'h-4 w-4 mr-2' }), 'Add Beneficiary'
                ),
                primary.length > 0 && el('div', { className: 'p-2 text-sm ' + (primaryTotal === 100 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700') },
                    'Primary Total: ' + primaryTotal.toFixed(1) + '% ' + (primaryTotal === 100 ? '✓' : '(must equal 100%)')
                ),
                contingent.length > 0 && el('div', { className: 'p-2 text-sm ' + (contingentTotal === 100 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700') },
                    'Contingent Total: ' + contingentTotal.toFixed(1) + '% ' + (contingentTotal === 100 ? '✓' : '(must equal 100%)')
                ),
                (editData.beneficiaries || []).length === 0 
                    ? el('p', { className: 'text-muted-foreground text-sm' }, 'No beneficiaries added')
                    : (editData.beneficiaries || []).map((ben, idx) => 
                        el('div', { key: ben.id || idx, className: 'p-4 border border-stone-200 space-y-3' },
                            el('div', { className: 'flex justify-between items-start' },
                                el('span', { className: 'text-sm font-medium' }, (ben.contingent ? 'Contingent ' : '') + 'Beneficiary ' + (idx + 1)),
                                el(Button, { type: 'button', variant: 'ghost', size: 'icon', onClick: () => removeBeneficiary(idx), className: 'h-8 w-8 text-destructive' },
                                    el(Trash2, { className: 'h-4 w-4' })
                                )
                            ),
                            el('div', { className: 'grid grid-cols-3 gap-4' },
                                el('div', { className: 'space-y-2' },
                                    el(Label, null, 'Name'),
                                    el(Input, { value: ben.name || '', onChange: e => updateBeneficiary(idx, 'name', e.target.value), className: 'rounded-none' })
                                ),
                                el('div', { className: 'space-y-2' },
                                    el(Label, null, 'Relationship'),
                                    el(Input, { value: ben.relationship || '', onChange: e => updateBeneficiary(idx, 'relationship', e.target.value), className: 'rounded-none' })
                                ),
                                el('div', { className: 'space-y-2' },
                                    el(Label, null, 'Percentage'),
                                    el(Input, { type: 'number', min: 0, max: 100, value: ben.percentage || 0, onChange: e => updateBeneficiary(idx, 'percentage', parseFloat(e.target.value) || 0), className: 'rounded-none' })
                                )
                            ),
                            el('label', { className: 'flex items-center gap-2' },
                                el('input', { type: 'checkbox', checked: ben.contingent || false, onChange: e => updateBeneficiary(idx, 'contingent', e.target.checked) }),
                                el('span', { className: 'text-sm' }, 'Contingent Beneficiary')
                            )
                        )
                    )
            );
        };
        
        // Assets form with add/edit/delete
        const addAsset = () => {
            const newAsset = { id: String(Date.now()), asset_type: 'real_estate', description: '', estimated_value: 0 };
            setEditData({...editData, assets: [...(editData.assets || []), newAsset]});
        };
        const updateAsset = (idx, field, value) => {
            const updated = [...editData.assets];
            updated[idx] = {...updated[idx], [field]: value};
            setEditData({...editData, assets: updated});
        };
        const removeAsset = (idx) => {
            setEditData({...editData, assets: editData.assets.filter((_, i) => i !== idx)});
        };
        
        const renderAssetsForm = () => {
            const total = (editData.assets || []).reduce((sum, a) => sum + (parseFloat(a.estimated_value) || 0), 0);
            
            return el('div', { className: 'space-y-4' },
                el(Button, { type: 'button', variant: 'outline', size: 'sm', onClick: addAsset, className: 'rounded-none mb-4' },
                    el(Plus, { className: 'h-4 w-4 mr-2' }), 'Add Asset'
                ),
                el('div', { className: 'p-2 bg-stone-100 text-sm font-medium' },
                    'Total Value: $' + total.toLocaleString()
                ),
                (editData.assets || []).length === 0 
                    ? el('p', { className: 'text-muted-foreground text-sm' }, 'No assets added')
                    : (editData.assets || []).map((asset, idx) => 
                        el('div', { key: asset.id || idx, className: 'p-4 border border-stone-200 space-y-3' },
                            el('div', { className: 'flex justify-between items-start' },
                                el('span', { className: 'text-sm font-medium' }, 'Asset ' + (idx + 1)),
                                el(Button, { type: 'button', variant: 'ghost', size: 'icon', onClick: () => removeAsset(idx), className: 'h-8 w-8 text-destructive' },
                                    el(Trash2, { className: 'h-4 w-4' })
                                )
                            ),
                            el('div', { className: 'grid grid-cols-3 gap-4' },
                                el('div', { className: 'space-y-2' },
                                    el(Label, null, 'Type'),
                                    el('select', { value: asset.asset_type || 'real_estate', onChange: e => updateAsset(idx, 'asset_type', e.target.value), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                                        el('option', { value: 'real_estate' }, 'Real Estate'),
                                        el('option', { value: 'bank_account' }, 'Bank Account'),
                                        el('option', { value: 'investment' }, 'Investment'),
                                        el('option', { value: 'retirement' }, 'Retirement'),
                                        el('option', { value: 'life_insurance' }, 'Life Insurance'),
                                        el('option', { value: 'vehicle' }, 'Vehicle'),
                                        el('option', { value: 'business' }, 'Business'),
                                        el('option', { value: 'personal_property' }, 'Personal Property'),
                                        el('option', { value: 'other' }, 'Other')
                                    )
                                ),
                                el('div', { className: 'space-y-2' },
                                    el(Label, null, 'Description'),
                                    el(Input, { value: asset.description || '', onChange: e => updateAsset(idx, 'description', e.target.value), className: 'rounded-none', placeholder: 'e.g., Primary residence' })
                                ),
                                el('div', { className: 'space-y-2' },
                                    el(Label, null, 'Est. Value ($)'),
                                    el(Input, { type: 'number', min: 0, value: asset.estimated_value || 0, onChange: e => updateAsset(idx, 'estimated_value', parseFloat(e.target.value) || 0), className: 'rounded-none' })
                                )
                            )
                        )
                    )
            );
        };
        
        // Key Contacts form - combines spouse, executor, POA, healthcare agent
        const renderKeyContactsForm = () => el('div', { className: 'space-y-6' },
            // Spouse section
            client.marital_status === 'married' && el('div', { className: 'p-4 border border-stone-200 space-y-4' },
                el('h4', { className: 'font-medium text-sm text-muted-foreground' }, 'SPOUSE'),
                el('div', { className: 'grid grid-cols-2 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Name'),
                        el(Input, { value: editData.spouse?.name || '', onChange: e => setEditData({...editData, spouse: {...editData.spouse, name: e.target.value}}), className: 'rounded-none' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Pronouns'),
                        el('select', { value: editData.spouse?.gender || '', onChange: e => setEditData({...editData, spouse: {...editData.spouse, gender: e.target.value}}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                            el('option', { value: '' }, 'Select...'),
                            el('option', { value: 'male' }, 'He/Him'),
                            el('option', { value: 'female' }, 'She/Her'),
                            el('option', { value: 'neutral' }, 'They/Them')
                        )
                    )
                ),
                el('div', { className: 'grid grid-cols-2 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Phone'),
                        el(Input, { type: 'tel', value: editData.spouse?.phone || '', onChange: e => setEditData({...editData, spouse: {...editData.spouse, phone: formatPhoneNumber(e.target.value)}}), className: 'rounded-none', placeholder: '(555) 555-5555' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Email'),
                        el(Input, { type: 'email', value: editData.spouse?.email || '', onChange: e => setEditData({...editData, spouse: {...editData.spouse, email: e.target.value}}), className: 'rounded-none' })
                    )
                )
            ),
            
            // Executor section
            el('div', { className: 'p-4 border border-stone-200 space-y-4' },
                el('h4', { className: 'font-medium text-sm text-muted-foreground' }, 'EXECUTOR'),
                el('div', { className: 'grid grid-cols-2 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Name'),
                        el(Input, { value: editData.executor?.primary_name || '', onChange: e => setEditData({...editData, executor: {...editData.executor, primary_name: e.target.value}}), className: 'rounded-none' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Pronouns'),
                        el('select', { value: editData.executor?.primary_gender || '', onChange: e => setEditData({...editData, executor: {...editData.executor, primary_gender: e.target.value}}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                            el('option', { value: '' }, 'Select...'),
                            el('option', { value: 'male' }, 'He/Him'),
                            el('option', { value: 'female' }, 'She/Her'),
                            el('option', { value: 'neutral' }, 'They/Them')
                        )
                    )
                ),
                el('div', { className: 'grid grid-cols-2 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Phone'),
                        el(Input, { type: 'tel', value: editData.executor?.primary_phone || '', onChange: e => setEditData({...editData, executor: {...editData.executor, primary_phone: formatPhoneNumber(e.target.value)}}), className: 'rounded-none', placeholder: '(555) 555-5555' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Relationship'),
                        el(Input, { value: editData.executor?.primary_relationship || '', onChange: e => setEditData({...editData, executor: {...editData.executor, primary_relationship: e.target.value}}), className: 'rounded-none' })
                    )
                )
            ),
            
            // POA section
            el('div', { className: 'p-4 border border-stone-200 space-y-4' },
                el('h4', { className: 'font-medium text-sm text-muted-foreground' }, 'POWER OF ATTORNEY'),
                el('div', { className: 'grid grid-cols-2 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Name'),
                        el(Input, { value: editData.power_of_attorney?.financial_poa_name || '', onChange: e => setEditData({...editData, power_of_attorney: {...editData.power_of_attorney, financial_poa_name: e.target.value}}), className: 'rounded-none' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Pronouns'),
                        el('select', { value: editData.power_of_attorney?.financial_poa_gender || '', onChange: e => setEditData({...editData, power_of_attorney: {...editData.power_of_attorney, financial_poa_gender: e.target.value}}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                            el('option', { value: '' }, 'Select...'),
                            el('option', { value: 'male' }, 'He/Him'),
                            el('option', { value: 'female' }, 'She/Her'),
                            el('option', { value: 'neutral' }, 'They/Them')
                        )
                    )
                ),
                el('div', { className: 'grid grid-cols-2 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Phone'),
                        el(Input, { type: 'tel', value: editData.power_of_attorney?.financial_poa_phone || '', onChange: e => setEditData({...editData, power_of_attorney: {...editData.power_of_attorney, financial_poa_phone: formatPhoneNumber(e.target.value)}}), className: 'rounded-none', placeholder: '(555) 555-5555' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Relationship'),
                        el(Input, { value: editData.power_of_attorney?.financial_poa_relationship || '', onChange: e => setEditData({...editData, power_of_attorney: {...editData.power_of_attorney, financial_poa_relationship: e.target.value}}), className: 'rounded-none' })
                    )
                )
            ),
            
            // Healthcare Agent section
            el('div', { className: 'p-4 border border-stone-200 space-y-4' },
                el('h4', { className: 'font-medium text-sm text-muted-foreground' }, 'HEALTHCARE AGENT'),
                el('div', { className: 'grid grid-cols-2 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Name'),
                        el(Input, { value: editData.healthcare_directive?.healthcare_agent_name || '', onChange: e => setEditData({...editData, healthcare_directive: {...editData.healthcare_directive, healthcare_agent_name: e.target.value}}), className: 'rounded-none' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Pronouns'),
                        el('select', { value: editData.healthcare_directive?.healthcare_agent_gender || '', onChange: e => setEditData({...editData, healthcare_directive: {...editData.healthcare_directive, healthcare_agent_gender: e.target.value}}), className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none' },
                            el('option', { value: '' }, 'Select...'),
                            el('option', { value: 'male' }, 'He/Him'),
                            el('option', { value: 'female' }, 'She/Her'),
                            el('option', { value: 'neutral' }, 'They/Them')
                        )
                    )
                ),
                el('div', { className: 'grid grid-cols-2 gap-4' },
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Phone'),
                        el(Input, { type: 'tel', value: editData.healthcare_directive?.healthcare_agent_phone || '', onChange: e => setEditData({...editData, healthcare_directive: {...editData.healthcare_directive, healthcare_agent_phone: formatPhoneNumber(e.target.value)}}), className: 'rounded-none', placeholder: '(555) 555-5555' })
                    ),
                    el('div', { className: 'space-y-2' },
                        el(Label, null, 'Relationship'),
                        el(Input, { value: editData.healthcare_directive?.healthcare_agent_relationship || '', onChange: e => setEditData({...editData, healthcare_directive: {...editData.healthcare_directive, healthcare_agent_relationship: e.target.value}}), className: 'rounded-none' })
                    )
                )
            )
        );
        
        const formRenderers = {
            intake: renderIntakeForm,
            attorney: renderAttorneyForm,
            personal: renderPersonalForm,
            spouse: renderSpouseForm,
            executor: renderExecutorForm,
            poa: renderPOAForm,
            healthcare: renderHealthcareForm,
            final_wishes: renderFinalWishesForm,
            children: renderChildrenForm,
            beneficiaries: renderBeneficiariesForm,
            assets: renderAssetsForm,
            key_contacts: renderKeyContactsForm
        };
        
        return el('div', { className: 'fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4' },
            el('div', { className: 'bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-none shadow-xl' },
                el('div', { className: 'sticky top-0 bg-white border-b p-4 flex items-center justify-between' },
                    el('h2', { className: 'font-serif text-xl' }, titles[editSection]),
                    el(Button, { variant: 'ghost', size: 'icon', onClick: cancelEdit }, el(X, { className: 'h-5 w-5' }))
                ),
                el('div', { className: 'p-6' },
                    formRenderers[editSection] ? formRenderers[editSection]() : null
                ),
                el('div', { className: 'sticky bottom-0 bg-white border-t p-4 flex justify-end gap-2' },
                    el(Button, { variant: 'outline', onClick: cancelEdit, className: 'rounded-none' }, 'Cancel'),
                    el(Button, { onClick: saveEdit, disabled: saving, className: 'rounded-none' },
                        saving ? el(Loader2, { className: 'h-4 w-4 mr-2 animate-spin' }) : el(Save, { className: 'h-4 w-4 mr-2' }),
                        'Save Changes'
                    )
                )
            )
        );
    };
    
    // Edit button component
    const EditBtn = ({ section }) => (
        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEdit(section)} data-testid={`edit-${section}-btn`}>
            <Pencil className="h-3.5 w-3.5" />
        </Button>
    );

    return (
        <div className="min-h-screen bg-[#FDFCF8]">
            <header className="sticky top-0 z-50 border-b border-stone-200 bg-white/80 backdrop-blur-sm">
                <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
                    <Link to="/dashboard" className="flex items-center gap-3">
                        <img src="/lion-logo.png" alt="BestWillsAndTrusts" className="h-10 w-10 object-contain" />
                        <span className="font-serif text-xl font-semibold hidden sm:block text-amber-600">BestWillsAndTrusts</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8">
                        <Link to="/dashboard" className="text-sm font-medium text-muted-foreground">Dashboard</Link>
                        <Link to="/clients" className="text-sm font-medium text-primary">Clients</Link>
                        <Link to="/documents" className="text-sm font-medium text-muted-foreground">Documents</Link>
                        <Link to="/templates" className="text-sm font-medium text-muted-foreground">Templates</Link>
                        {auth.user?.role === 'admin' && <Link to="/admin/users" className="text-sm font-medium text-muted-foreground">Users</Link>}
                    </nav>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground hidden sm:block">{auth.user?.name}</span>
                        <Button variant="ghost" size="sm" onClick={() => auth.logout()} className="rounded-none" data-testid="logout-btn">
                            <LogOut className="h-4 w-4 mr-2" />Sign Out
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <Link to="/clients" className="inline-flex items-center text-sm text-muted-foreground mb-4">
                        <ChevronLeft className="h-4 w-4 mr-1" />Back to Clients
                    </Link>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <h1 className="font-serif text-3xl" data-testid="client-full-name">
                                    {fmtFullName(client.first_name, client.middle_name, client.last_name)}
                                </h1>
                                <Badge 
                                    variant={client.status === 'active_retained' ? 'default' : client.status === 'active_not_retained' ? 'secondary' : 'outline'}
                                    className={`rounded-none ${
                                        client.status === 'active_retained' ? 'bg-amber-500 hover:bg-amber-500' : 
                                        client.status === 'active_not_retained' ? 'bg-blue-500 hover:bg-blue-500 text-white' : 
                                        'bg-stone-200 text-stone-600'
                                    }`}
                                    data-testid="client-status-badge"
                                >
                                    {client.status === 'active_retained' ? 'Active & Retained' : 
                                     client.status === 'active_not_retained' ? 'Active & Not Retained' : 
                                     'Inactive'}
                                </Badge>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                {client.email && <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{client.email}</span>}
                                {client.phone && <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{client.phone}</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-3">
                                <span className="text-sm text-muted-foreground">Assigned to:</span>
                                <div className="relative">
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="rounded-none h-8"
                                        onClick={() => setShowAssignDropdown(!showAssignDropdown)}
                                        data-testid="assign-client-btn"
                                    >
                                        <User className="h-3.5 w-3.5 mr-1.5" />
                                        {client.assigned_to_name || 'Unassigned'}
                                    </Button>
                                    {showAssignDropdown && (
                                        <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-stone-200 shadow-lg z-50" data-testid="assign-dropdown">
                                            <div className="p-2 border-b border-stone-100">
                                                <span className="text-xs font-medium text-muted-foreground">Assign to</span>
                                            </div>
                                            <div className="max-h-48 overflow-y-auto">
                                                <button 
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 flex items-center gap-2"
                                                    onClick={() => assignClient(null)}
                                                >
                                                    <span className="text-muted-foreground">Unassigned</span>
                                                </button>
                                                {users.map(u => (
                                                    <button 
                                                        key={u.id}
                                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-stone-50 flex items-center gap-2 ${client.assigned_to === u.id ? 'bg-amber-50' : ''}`}
                                                        onClick={() => assignClient(u.id)}
                                                    >
                                                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                                                        <span>{u.name}</span>
                                                        {client.assigned_to === u.id && <CheckCircle className="h-3.5 w-3.5 text-amber-600 ml-auto" />}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Button variant="outline" className="rounded-none" onClick={() => navigate('/clients/' + id + '/edit')} data-testid="edit-client-btn">
                            <Edit className="h-4 w-4 mr-2" />Edit Client
                        </Button>
                    </div>
                </motion.div>

                <Tabs defaultValue="intake" className="space-y-6">
                    <TabsList className="rounded-none bg-secondary p-1">
                        <TabsTrigger value="intake" className="rounded-none" data-testid="tab-intake">Client Info</TabsTrigger>
                        <TabsTrigger value="communications" className="rounded-none" data-testid="tab-communications">Communications</TabsTrigger>
                        <TabsTrigger value="documents" className="rounded-none" data-testid="tab-documents">Documents</TabsTrigger>
                    </TabsList>

                    {/* CLIENT INFO TAB */}
                    <TabsContent value="intake" className="space-y-6">
                        {/* Intake & Attorney Notes Summary */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card className="rounded-none border-amber-200 bg-amber-50/30">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="font-serif text-lg text-amber-800">Intake Information</CardTitle>
                                    <EditBtn section="intake" />
                                </CardHeader>
                                <CardContent className="space-y-3 text-sm">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant="outline" className="rounded-none capitalize">{(client.status || 'active_retained').replace(/_/g, ' ')}</Badge></div>
                                    {client.attorney_assigned && <div className="flex justify-between"><span className="text-muted-foreground">Attorney</span><span className="font-medium">{client.attorney_assigned}</span></div>}
                                    {client.intake_coordinator && <div className="flex justify-between"><span className="text-muted-foreground">Intake Coordinator</span><span>{client.intake_coordinator}</span></div>}
                                    {client.referral_source && <div className="flex justify-between"><span className="text-muted-foreground">Referral Source</span><span>{client.referral_source}</span></div>}
                                    {client.consultation_date && <div className="flex justify-between"><span className="text-muted-foreground">Consultation Date</span><span>{fmtDate(client.consultation_date)}</span></div>}
                                    {client.fee_amount && <div className="flex justify-between"><span className="text-muted-foreground">Fee Amount</span><span className="font-medium">{client.fee_amount}</span></div>}
                                    {client.services_requested && Object.keys(client.services_requested).some(k => client.services_requested[k]) && (
                                        <div className="pt-2 border-t border-amber-200">
                                            <span className="text-muted-foreground block mb-2">Services Requested</span>
                                            <div className="flex flex-wrap gap-1">
                                                {client.services_requested.will && <Badge variant="outline" className="rounded-none text-xs">Will</Badge>}
                                                {client.services_requested.trust && <Badge variant="outline" className="rounded-none text-xs">Trust</Badge>}
                                                {client.services_requested.poa && <Badge variant="outline" className="rounded-none text-xs">POA</Badge>}
                                                {client.services_requested.amd && <Badge variant="outline" className="rounded-none text-xs">AMD</Badge>}
                                                {client.services_requested.other && <Badge variant="outline" className="rounded-none text-xs">Other</Badge>}
                                            </div>
                                        </div>
                                    )}
                                    {client.intake_notes && <div className="pt-2 border-t border-amber-200"><span className="text-muted-foreground block mb-1">Intake Notes</span><p className="text-sm">{client.intake_notes}</p></div>}
                                </CardContent>
                            </Card>
                            <Card className="rounded-none border-blue-200 bg-blue-50/30">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="font-serif text-lg text-blue-800">Attorney Notes</CardTitle>
                                    <EditBtn section="attorney" />
                                </CardHeader>
                                <CardContent className="space-y-3 text-sm">
                                    {client.attorney_recommendation && <div><span className="text-muted-foreground block mb-1">What Was Recommended</span><p>{client.attorney_recommendation}</p></div>}
                                    {client.recommendation_reason && <div className="pt-2 border-t border-blue-200"><span className="text-muted-foreground block mb-1">Why Recommended</span><p>{client.recommendation_reason}</p></div>}
                                    {client.cost_quoted && <div className="pt-2 border-t border-blue-200"><span className="text-muted-foreground block mb-1">Cost Quoted</span><span className="font-medium text-lg">{client.cost_quoted}</span></div>}
                                    {!client.attorney_recommendation && !client.recommendation_reason && !client.cost_quoted && <p className="text-muted-foreground">No attorney notes recorded</p>}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Personal Info & Key Contacts */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Card className="rounded-none border-stone-200">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="font-serif text-lg flex items-center gap-2"><User className="h-5 w-5" />Personal Info</CardTitle>
                                    <EditBtn section="personal" />
                                </CardHeader>
                                <CardContent className="space-y-3 text-sm">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Full Name</span><span className="font-medium">{fmtFullName(client.first_name, client.middle_name, client.last_name)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">DOB</span><span>{fmtDate(client.date_of_birth)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Marital Status</span><Badge variant="outline" className="rounded-none capitalize">{client.marital_status}</Badge></div>
                                    {client.locality_name && <div className="flex justify-between"><span className="text-muted-foreground">Residence</span><span>{client.locality_type === 'city' ? 'City' : 'County'} of {client.locality_name}</span></div>}
                                    {client.phone && <div className="flex items-center gap-2 pt-2 border-t"><Phone className="h-3.5 w-3.5 text-muted-foreground" /><span>{client.phone}</span></div>}
                                    {client.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" /><span>{client.email}</span></div>}
                                    {client.address && <div className="pt-2 border-t"><span className="text-muted-foreground flex items-center gap-1 mb-1"><MapPin className="h-3.5 w-3.5" />Address</span><p>{client.address}, {client.city}, {client.state} {client.zip_code}</p></div>}
                                </CardContent>
                            </Card>
                            <Card className="rounded-none border-stone-200">
                                <CardHeader><CardTitle className="font-serif text-lg flex items-center gap-2"><Briefcase className="h-5 w-5" />Estate Summary</CardTitle></CardHeader>
                                <CardContent className="space-y-3 text-sm">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Assets</span><span className="font-semibold">{(client.assets || []).length}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Value</span><span className="font-semibold">{fmtMoney(totalVal)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Beneficiaries</span><span className="font-semibold">{(client.beneficiaries || []).length}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Children</span><span className="font-semibold">{(client.children || []).length}</span></div>
                                    {client.executor?.primary_name && <div className="pt-2 border-t"><span className="text-muted-foreground">Executor</span><p className="font-medium">{client.executor.primary_name}</p></div>}
                                </CardContent>
                            </Card>
                            <Card className="rounded-none border-stone-200">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="font-serif text-lg flex items-center gap-2"><Users className="h-5 w-5" />Key Contacts</CardTitle>
                                    <EditBtn section="key_contacts" />
                                </CardHeader>
                                <CardContent className="space-y-4 text-sm">
                                    {client.marital_status === 'married' && client.spouse?.name && <div className="pb-3 border-b border-stone-100">
                                        <span className="text-muted-foreground">Spouse</span>
                                        <p className="font-medium">{client.spouse.name}</p>
                                        {client.spouse.phone && <p className="text-xs flex items-center gap-1 mt-1"><Phone className="h-3 w-3" />{client.spouse.phone}</p>}
                                    </div>}
                                    {client.power_of_attorney?.financial_poa_name && <div className="pb-3 border-b border-stone-100">
                                        <span className="text-muted-foreground">POA</span>
                                        <p className="font-medium">{client.power_of_attorney.financial_poa_name}</p>
                                        {client.power_of_attorney.financial_poa_phone && <p className="text-xs flex items-center gap-1 mt-1"><Phone className="h-3 w-3" />{client.power_of_attorney.financial_poa_phone}</p>}
                                    </div>}
                                    {client.healthcare_directive?.healthcare_agent_name && <div>
                                        <span className="text-muted-foreground">Healthcare Agent</span>
                                        <p className="font-medium">{client.healthcare_directive.healthcare_agent_name}</p>
                                        {client.healthcare_directive.healthcare_agent_phone && <p className="text-xs flex items-center gap-1 mt-1"><Phone className="h-3 w-3" />{client.healthcare_directive.healthcare_agent_phone}</p>}
                                    </div>}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Family - Spouse, Children, Beneficiaries */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card className="rounded-none border-stone-200">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="font-serif text-lg flex items-center gap-2"><Users className="h-5 w-5" />Children ({(client.children || []).length})</CardTitle>
                                    <EditBtn section="children" />
                                </CardHeader>
                                <CardContent>
                                    {(client.children || []).length > 0 ? (
                                        <div className="space-y-3">
                                            {(client.children || []).map((c, i) => (
                                                <div key={i} className={'p-3 border ' + (c.disinherited ? 'border-red-300 bg-red-50' : 'border-stone-200')}>
                                                    <p className="font-medium" data-testid={'child-name-' + i}>{c.name}</p>
                                                    <p className="text-sm text-muted-foreground">DOB: {c.date_of_birth ? fmtDate(c.date_of_birth) : 'Not provided'}</p>
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {c.minor && <Badge variant="outline" className="rounded-none text-xs">Minor</Badge>}
                                                        {c.special_needs && <Badge variant="outline" className="rounded-none text-xs">Special Needs</Badge>}
                                                        {c.disinherited && <Badge variant="destructive" className="rounded-none text-xs">Disinherited</Badge>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : <p className="text-muted-foreground text-sm">No children listed</p>}
                                </CardContent>
                            </Card>
                            <Card className="rounded-none border-stone-200">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="font-serif text-lg flex items-center gap-2"><Heart className="h-5 w-5" />Beneficiaries ({(client.beneficiaries || []).length})</CardTitle>
                                    <EditBtn section="beneficiaries" />
                                </CardHeader>
                                <CardContent>
                                    {(client.beneficiaries || []).length > 0 ? (
                                        <div className="space-y-3">
                                            {(client.beneficiaries || []).map((b, i) => (
                                                <div key={i} className="p-3 border border-stone-200 flex justify-between items-center">
                                                    <div>
                                                        <p className="font-medium" data-testid={'beneficiary-name-' + i}>{b.name}</p>
                                                        <p className="text-sm text-muted-foreground">{b.relationship || 'Relationship not specified'}</p>
                                                    </div>
                                                    <Badge variant="secondary" className="rounded-none font-semibold">{b.percentage || 0}%</Badge>
                                                </div>
                                            ))}
                                        </div>
                                    ) : <p className="text-muted-foreground text-sm">No beneficiaries listed</p>}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Assets */}
                        <Card className="rounded-none border-stone-200">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <div>
                                    <CardTitle className="font-serif text-lg flex items-center gap-2"><Briefcase className="h-5 w-5" />Assets ({(client.assets || []).length})</CardTitle>
                                    <CardDescription>Total estimated value: {fmtMoney(totalVal)}</CardDescription>
                                </div>
                                <EditBtn section="assets" />
                            </CardHeader>
                            <CardContent>
                                {(client.assets || []).length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {(client.assets || []).map((a, i) => (
                                            <div key={i} className="p-4 border border-stone-200">
                                                <div className="flex justify-between">
                                                    <div><p className="font-medium">{a.description || 'Asset ' + (i+1)}</p><p className="text-sm text-muted-foreground capitalize">{a.asset_type?.replace('_', ' ')}</p></div>
                                                    <span className="font-semibold">{fmtMoney(a.estimated_value)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : <p className="text-muted-foreground text-sm">No assets listed</p>}
                            </CardContent>
                        </Card>

                        {/* Estate Planning Details - Executor, POA, Healthcare, Final Wishes */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Card className="rounded-none border-stone-200">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="font-serif text-lg flex items-center gap-2"><Gavel className="h-5 w-5" />Executor & POA</CardTitle>
                                    <div className="flex gap-1">
                                        <EditBtn section="executor" />
                                        <EditBtn section="poa" />
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4 text-sm">
                                    <div>
                                        <span className="text-muted-foreground font-medium">Primary Executor</span>
                                        {client.executor?.primary_name ? (
                                            <div className="mt-1">
                                                <p className="font-medium">{client.executor.primary_name}</p>
                                                {client.executor.primary_relationship && <p className="text-xs text-muted-foreground">{client.executor.primary_relationship}</p>}
                                            </div>
                                        ) : <p className="text-muted-foreground">Not specified</p>}
                                    </div>
                                    {client.executor?.alternate_name && <div className="pt-3 border-t">
                                        <span className="text-muted-foreground font-medium">Alternate Executor</span>
                                        <p className="font-medium mt-1">{client.executor.alternate_name}</p>
                                    </div>}
                                    <div className="pt-3 border-t">
                                        <span className="text-muted-foreground font-medium">Power of Attorney</span>
                                        {client.power_of_attorney?.financial_poa_name ? (
                                            <div className="mt-1">
                                                <p className="font-medium">{client.power_of_attorney.financial_poa_name}</p>
                                                {client.power_of_attorney.financial_poa_relationship && <p className="text-xs text-muted-foreground">{client.power_of_attorney.financial_poa_relationship}</p>}
                                                <div className="flex gap-2 mt-1">
                                                    {client.power_of_attorney.durable && <Badge variant="outline" className="rounded-none text-xs">Durable</Badge>}
                                                    {client.power_of_attorney.springing && <Badge variant="outline" className="rounded-none text-xs">Springing</Badge>}
                                                </div>
                                            </div>
                                        ) : <p className="text-muted-foreground">Not specified</p>}
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="rounded-none border-stone-200">
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="font-serif text-lg flex items-center gap-2"><Heart className="h-5 w-5" />Healthcare & Final Wishes</CardTitle>
                                    <div className="flex gap-1">
                                        <EditBtn section="healthcare" />
                                        <EditBtn section="final_wishes" />
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4 text-sm">
                                    <div>
                                        <span className="text-muted-foreground font-medium">Healthcare Agent</span>
                                        {client.healthcare_directive?.healthcare_agent_name ? (
                                            <div className="mt-1">
                                                <p className="font-medium">{client.healthcare_directive.healthcare_agent_name}</p>
                                                {client.healthcare_directive.healthcare_agent_relationship && <p className="text-xs text-muted-foreground">{client.healthcare_directive.healthcare_agent_relationship}</p>}
                                            </div>
                                        ) : <p className="text-muted-foreground">Not specified</p>}
                                    </div>
                                    <div className="pt-3 border-t flex flex-wrap gap-2">
                                        {client.healthcare_directive?.has_living_will && <Badge variant="outline" className="rounded-none text-xs">Living Will</Badge>}
                                        {client.healthcare_directive?.dnr_order && <Badge variant="outline" className="rounded-none text-xs">DNR Order</Badge>}
                                        {client.healthcare_directive?.organ_donor && <Badge variant="outline" className="rounded-none text-xs">Organ Donor</Badge>}
                                    </div>
                                    <div className="pt-3 border-t">
                                        <span className="text-muted-foreground font-medium">Final Wishes</span>
                                        <div className="mt-1 space-y-1">
                                            <div className="flex justify-between"><span className="text-muted-foreground">Disposition</span><span className="capitalize">{client.final_wishes?.disposition_method || 'Cremation'}</span></div>
                                            <div className="flex justify-between"><span className="text-muted-foreground">Distribution</span><span className="capitalize">{client.final_wishes?.distribution_type?.replace('_', ' ') || 'Per Stirpes'}</span></div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* COMMUNICATIONS TAB */}
                    <TabsContent value="communications" className="space-y-6">
                        <Card className="rounded-none border-stone-200">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="font-serif text-lg flex items-center gap-2"><MessageSquare className="h-5 w-5" />Communication Log</CardTitle>
                                    <Button variant="outline" size="sm" className="rounded-none" onClick={() => setShowAddComm(true)} data-testid="add-communication-btn">
                                        <Plus className="h-4 w-4 mr-2" />Add Entry
                                    </Button>
                                </div>
                                <CardDescription>Track all calls, emails, meetings, and deliverables</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {/* Add Communication Form */}
                                {showAddComm && (
                                    <div className="mb-6 p-4 border border-amber-200 bg-amber-50/50 space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="space-y-2">
                                                <Label>Contact Type</Label>
                                                <select 
                                                    value={newComm.contact_type} 
                                                    onChange={e => setNewComm({...newComm, contact_type: e.target.value})}
                                                    className="w-full h-10 px-3 border border-stone-200 bg-white rounded-none"
                                                >
                                                    <option value="call">Phone Call</option>
                                                    <option value="email">Email</option>
                                                    <option value="meeting">Meeting</option>
                                                    <option value="letter">Letter</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Contact Date</Label>
                                                <Input 
                                                    type="date" 
                                                    value={newComm.contact_date} 
                                                    onChange={e => setNewComm({...newComm, contact_date: e.target.value})}
                                                    className="rounded-none"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Due Date (if deliverable)</Label>
                                                <Input 
                                                    type="date" 
                                                    value={newComm.due_date} 
                                                    onChange={e => setNewComm({...newComm, due_date: e.target.value})}
                                                    className="rounded-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Deliverable (document or action due)</Label>
                                            <Input 
                                                value={newComm.deliverable} 
                                                onChange={e => setNewComm({...newComm, deliverable: e.target.value})}
                                                className="rounded-none"
                                                placeholder="e.g., Will draft, POA review, Signed documents..."
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Notes</Label>
                                            <Textarea 
                                                value={newComm.notes} 
                                                onChange={e => setNewComm({...newComm, notes: e.target.value})}
                                                className="rounded-none min-h-[80px]"
                                                placeholder="Details of the communication..."
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button size="sm" className="rounded-none" onClick={addCommunication}>Save Entry</Button>
                                            <Button size="sm" variant="outline" className="rounded-none" onClick={() => setShowAddComm(false)}>Cancel</Button>
                                        </div>
                                    </div>
                                )}

                                {/* Pending Deliverables */}
                                {pendingDeliverables.length > 0 && (
                                    <div className="mb-6">
                                        <h4 className="text-sm font-medium text-amber-700 mb-3 flex items-center gap-2">
                                            <Clock className="h-4 w-4" />Pending Deliverables ({pendingDeliverables.length})
                                        </h4>
                                        <div className="space-y-2">
                                            {pendingDeliverables.map(log => (
                                                <div key={log.id} className="p-3 border border-amber-200 bg-amber-50/30 flex items-start justify-between">
                                                    <div className="flex items-start gap-3">
                                                        <Checkbox checked={log.completed} onCheckedChange={() => toggleComplete(log.id)} />
                                                        <div>
                                                            <p className="font-medium text-sm">{log.deliverable}</p>
                                                            <p className="text-xs text-muted-foreground">Due: {fmtDate(log.due_date)}</p>
                                                            {log.notes && <p className="text-xs text-muted-foreground mt-1">{log.notes}</p>}
                                                        </div>
                                                    </div>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteComm(log.id)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Communication History */}
                                <div>
                                    <h4 className="text-sm font-medium text-muted-foreground mb-3">Communication History</h4>
                                    {communications.length === 0 ? (
                                        <p className="text-sm text-muted-foreground">No communications logged yet</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {communications.map(log => (
                                                <div key={log.id} className={'p-3 border flex items-start justify-between ' + (log.completed ? 'border-green-200 bg-green-50/30' : 'border-stone-200')}>
                                                    <div className="flex items-start gap-3">
                                                        {log.deliverable && <Checkbox checked={log.completed} onCheckedChange={() => toggleComplete(log.id)} />}
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <Badge variant="outline" className="rounded-none text-xs capitalize">{log.contact_type}</Badge>
                                                                <span className="text-xs text-muted-foreground">{fmtDate(log.contact_date)}</span>
                                                                {log.completed && <CheckCircle className="h-4 w-4 text-green-600" />}
                                                            </div>
                                                            {log.deliverable && <p className="font-medium text-sm mt-1">{log.deliverable}</p>}
                                                            {log.due_date && <p className="text-xs text-muted-foreground">Due: {fmtDate(log.due_date)}</p>}
                                                            {log.notes && <p className="text-xs text-muted-foreground mt-1">{log.notes}</p>}
                                                        </div>
                                                    </div>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteComm(log.id)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* DOCUMENTS TAB */}
                    <TabsContent value="documents" className="space-y-6">
                        {/* Generate All Essential Documents Button */}
                        <Card className="rounded-none border-amber-400 bg-amber-50/30">
                            <CardHeader>
                                <CardTitle className="font-serif text-lg flex items-center gap-2">
                                    <Gavel className="h-5 w-5 text-amber-600" />
                                    Quick Document Generation
                                </CardTitle>
                                <CardDescription>Generate Will, Power of Attorney, and Advance Medical Directive at once</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Button 
                                    className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white rounded-none" 
                                    onClick={genAllEssentialDocs} 
                                    disabled={gen}
                                    data-testid="generate-all-docs-btn"
                                >
                                    {gen ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                                    Generate Will, POA & Adv. Med. Directive
                                </Button>
                            </CardContent>
                        </Card>

                        <Card className="rounded-none border-stone-200">
                            <CardHeader>
                                <CardTitle className="font-serif text-lg">Generate Documents</CardTitle>
                                <CardDescription>Select a template and generate documents</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Wills */}
                                <div className="p-4 border border-stone-200 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <Label className="font-medium">Will Templates</Label>
                                        {templateRecommendation && (
                                            <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-3 py-1 border border-amber-200">
                                                <Lightbulb className="h-4 w-4" />
                                                <span className="text-sm font-medium">Recommended: {templateRecommendation.display_name}</span>
                                            </div>
                                        )}
                                    </div>
                                    {templateRecommendation && (
                                        <p className="text-sm text-stone-500 italic">
                                            {templateRecommendation.reason}
                                        </p>
                                    )}
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <select 
                                            className="flex-1 h-10 px-3 border border-stone-200 bg-white rounded-none text-sm"
                                            value={selectedTemplates.wills}
                                            onChange={(e) => setSelectedTemplates({...selectedTemplates, wills: e.target.value})}
                                            data-testid="will-template-select"
                                        >
                                            {templates.filter(t => t.category === 'wills').map(t => (
                                                <option key={t.name} value={t.name}>
                                                    {t.display_name}{templateRecommendation?.template_name === t.name ? ' ✓ Recommended' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <Button variant="outline" className="rounded-none" onClick={() => genDoc(selectedTemplates.wills)} disabled={gen}>
                                            <ScrollText className="h-4 w-4 mr-2" />Generate
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Trusts */}
                                <div className="p-4 border border-stone-200 space-y-3">
                                    <Label className="font-medium">Trust Templates</Label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <select 
                                            className="flex-1 h-10 px-3 border border-stone-200 bg-white rounded-none text-sm"
                                            value={selectedTemplates.trusts}
                                            onChange={(e) => setSelectedTemplates({...selectedTemplates, trusts: e.target.value})}
                                        >
                                            {templates.filter(t => t.category === 'trusts').map(t => (
                                                <option key={t.name} value={t.name}>{t.display_name}</option>
                                            ))}
                                        </select>
                                        <Button variant="outline" className="rounded-none" onClick={() => genDoc(selectedTemplates.trusts)} disabled={gen}>
                                            <ScrollText className="h-4 w-4 mr-2" />Generate
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Powers of Attorney */}
                                <div className="p-4 border border-stone-200 space-y-3">
                                    <Label className="font-medium">Power of Attorney Templates</Label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <select 
                                            className="flex-1 h-10 px-3 border border-stone-200 bg-white rounded-none text-sm"
                                            value={selectedTemplates.poa}
                                            onChange={(e) => setSelectedTemplates({...selectedTemplates, poa: e.target.value})}
                                        >
                                            {templates.filter(t => t.category === 'poa').map(t => (
                                                <option key={t.name} value={t.name}>{t.display_name}</option>
                                            ))}
                                        </select>
                                        <Button variant="outline" className="rounded-none" onClick={() => genDoc(selectedTemplates.poa)} disabled={gen}>
                                            <ScrollText className="h-4 w-4 mr-2" />Generate
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Advance Medical Directive */}
                                <div className="p-4 border border-stone-200 space-y-3">
                                    <Label className="font-medium">Advance Medical Directive</Label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <select 
                                            className="flex-1 h-10 px-3 border border-stone-200 bg-white rounded-none text-sm"
                                            value={selectedTemplates.amd}
                                            onChange={(e) => setSelectedTemplates({...selectedTemplates, amd: e.target.value})}
                                        >
                                            {templates.filter(t => t.category === 'amd').map(t => (
                                                <option key={t.name} value={t.name}>{t.display_name}</option>
                                            ))}
                                        </select>
                                        <Button variant="outline" className="rounded-none" onClick={() => genDoc(selectedTemplates.amd)} disabled={gen}>
                                            <ScrollText className="h-4 w-4 mr-2" />Generate
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Other Documents (Letters, etc.) */}
                                <div className="p-4 border border-stone-200 space-y-3">
                                    <Label className="font-medium">Other Documents</Label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <select 
                                            className="flex-1 h-10 px-3 border border-stone-200 bg-white rounded-none text-sm"
                                            value={selectedTemplates.other}
                                            onChange={(e) => setSelectedTemplates({...selectedTemplates, other: e.target.value})}
                                        >
                                            {templates.filter(t => t.category === 'other' || t.category === 'letters').map(t => (
                                                <option key={t.name} value={t.name}>{t.display_name}</option>
                                            ))}
                                        </select>
                                        <Button variant="outline" className="rounded-none" onClick={() => genDoc(selectedTemplates.other)} disabled={gen}>
                                            <ScrollText className="h-4 w-4 mr-2" />Generate
                                        </Button>
                                    </div>
                                </div>
                                
                                {gen && <div className="p-4 bg-primary/5 flex items-center gap-3"><Loader2 className="h-5 w-5 animate-spin text-primary" /><span className="text-sm">Generating...</span></div>}
                            </CardContent>
                        </Card>
                        <Card className="rounded-none border-stone-200">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="font-serif text-lg">Generated Documents ({docs.length})</CardTitle>
                                {docs.filter(d => !d.finalized).length > 0 && (
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="rounded-none text-destructive hover:bg-destructive/10"
                                        onClick={deleteAllDrafts}
                                    >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Delete All Drafts
                                    </Button>
                                )}
                            </CardHeader>
                            <CardContent>
                                {docs.length > 0 ? (
                                    <div className="space-y-2">
                                        {docs.map(d => (
                                            <div key={d.id} className={'p-4 border flex justify-between items-center ' + (d.finalized ? 'border-amber-400 bg-amber-50/50' : 'border-stone-200')}>
                                                <div className="flex items-center gap-3">
                                                    {d.finalized && <Star className="h-5 w-5 text-amber-500 fill-amber-500" />}
                                                    <div>
                                                        <p className="font-medium">{d.title}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            Version {d.version} • {fmtDateTime(d.created_at)}
                                                            {d.finalized && <span className="ml-2 text-amber-600 font-medium">• Final Version</span>}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        className={'rounded-none ' + (d.finalized ? 'text-amber-600 border-amber-400 hover:bg-amber-100' : '')}
                                                        onClick={() => toggleFinalize(d.id)}
                                                        title={d.finalized ? 'Unmark as final' : 'Mark as final version'}
                                                    >
                                                        <Star className={'h-4 w-4 ' + (d.finalized ? 'fill-amber-500' : '')} />
                                                    </Button>
                                                    <Button variant="outline" size="sm" className="rounded-none" asChild>
                                                        <Link to={'/documents/' + d.id}><FileText className="h-4 w-4" /></Link>
                                                    </Button>
                                                    <Button variant="outline" size="sm" className="rounded-none" onClick={async () => {
                                                        try {
                                                            const response = await axios.get(API_URL + '/api/documents/' + d.id + '/export/word', { responseType: 'blob' });
                                                            const url = window.URL.createObjectURL(new Blob([response.data]));
                                                            const link = document.createElement('a');
                                                            link.href = url;
                                                            link.setAttribute('download', d.title.replace(/ /g, '_') + '.docx');
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            link.remove();
                                                            window.URL.revokeObjectURL(url);
                                                        } catch (e) { toast.error('Download failed'); }
                                                    }}>
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                    {!d.finalized && (
                                                        <Button 
                                                            variant="outline" 
                                                            size="sm" 
                                                            className="rounded-none text-destructive hover:bg-destructive/10"
                                                            onClick={() => deleteDoc(d.id, d.title)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : <p className="text-muted-foreground text-sm">No documents generated yet</p>}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>
            
            {/* Edit Modal */}
            {EditModal()}
        </div>
    );
}

export default ClientDetail;
